import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import { AnnouncementAudience } from "../../features/announcements/contracts/AnnouncementAudience";
import { AnnouncementAudiencePreviewResponse } from "../../features/announcements/contracts/AnnouncementAudiencePreviewResponse";
import { fetchAnnouncementAudience } from "../../features/announcements/slice-extensions/announcements-slice-extension";

interface Props {
  audience: AnnouncementAudience;
  sessionId?: string;
}

/**
 * The real reach of the chosen audience, resolved server-side before sending.
 * Unsubscribed and unreachable members are called out separately: one is the
 * member's own decision, the other is a missing address the instructor can fix.
 */
const AnnouncementAudienceSummary = ({ audience, sessionId }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [preview, setPreview] = useState<AnnouncementAudiencePreviewResponse | null>(null);

  useEffect(() => {
    if (audience === "Session" && !sessionId) {
      setPreview(null);
      return;
    }

    let active = true;
    dispatch(fetchAnnouncementAudience({ audience, sessionId }))
      .unwrap()
      .then((result) => {
        if (active) setPreview(result);
      })
      .catch(() => {
        if (active) setPreview(null);
      });

    return () => {
      active = false;
    };
  }, [audience, sessionId, dispatch]);

  if (!preview) {
    return null;
  }

  const excluded = [
    preview.optedOutCount > 0
      ? t("announcements.audienceUnsubscribed", "{{count}} unsubscribed", {
          count: preview.optedOutCount,
        })
      : null,
    preview.unreachableCount > 0
      ? t("announcements.audienceNoEmail", "{{count}} without an email address", {
          count: preview.unreachableCount,
        })
      : null,
  ].filter(Boolean);

  return (
    <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-grey-40 dark:text-grey-80">
      <Users size={16} />
      <span className="font-medium text-grey-20 dark:text-white">
        {t("announcements.audienceReach", "{{count}} will receive this", {
          count: preview.emailCount,
        })}
      </span>
      {excluded.length > 0 && <span>({excluded.join(" · ")})</span>}
    </p>
  );
};

export default AnnouncementAudienceSummary;
